import { useEffect, useRef, type RefObject } from 'react'

const FOCUSABLE = 'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])'

/**
 * Standard modal focus behaviour, shared by every overlay in this app
 * (Dialog.tsx, CommandPalette.tsx): moves focus into the panel when it opens,
 * keeps Tab/Shift+Tab cycling within it instead of escaping to the page
 * behind, and returns focus to whatever had it before the panel opened when
 * it closes. The design's own binding requirement — "Dialogs trap focus and
 * return it on close" — previously only half-held: focus moved in and back
 * out again, but Tab could still walk straight through the panel into the
 * sidebar/content behind it while it was open.
 *
 * Escape is deliberately not handled here — every caller already has its own
 * Escape listener, sometimes doing more than closing (the command palette
 * only needs to close; a future confirm dialog might need to ask first).
 */
export function useFocusTrap(open: boolean, panelRef: RefObject<HTMLElement | null>): void {
  const returnFocusTo = useRef<Element | null>(null)

  useEffect(() => {
    if (!open) return
    const panel = panelRef.current
    returnFocusTo.current = document.activeElement

    const focusables = () => Array.from(panel?.querySelectorAll<HTMLElement>(FOCUSABLE) ?? [])
    const first = focusables()[0]
    ;(first ?? panel)?.focus()

    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key !== 'Tab' || !panel) return
      const items = focusables()
      if (items.length === 0) {
        event.preventDefault()
        return
      }
      const firstItem = items[0]!
      const lastItem = items[items.length - 1]!
      if (event.shiftKey && document.activeElement === firstItem) {
        event.preventDefault()
        lastItem.focus()
      } else if (!event.shiftKey && document.activeElement === lastItem) {
        event.preventDefault()
        firstItem.focus()
      }
    }
    document.addEventListener('keydown', onKeyDown)

    return () => {
      document.removeEventListener('keydown', onKeyDown)
      // Send focus back where it came from, or the user is dropped at the top
      // of the document every time they dismiss the panel.
      ;(returnFocusTo.current as HTMLElement | null)?.focus?.()
    }
  }, [open, panelRef])
}
